import React from "react";
import Badge from "src/components/ui/Badge";
import type { BadgeTone } from "src/components/ui/Badge";

type Props = {
  status: string;
};

/**
 * A status chip.
 *
 * The chip itself is the shared one, so a status looks like a status wherever
 * it is read — the positions table, the investment details, the transactions
 * ledger. What stays here is the mapping: which statuses mean the same thing
 * to a reader, and which of them are still moving.
 */

// Status ENUM
// PENDING OPENING  OPEN  CANCELED  CLOSING  EXITING  PARTIAL-EXITING  CLOSED  TOP-UP REBALANCING

const statusTone = (status: string): BadgeTone => {
  const toneByStatus: Record<string, BadgeTone> = {
    PENDING: "warning",
    OPENING: "warning",
    OPEN: "success",
    CANCELED: "error",
    CLOSED: "error",
    FAILED: "error",
  };
  // Default to success if status is not in toneByStatus
  return toneByStatus[status] || "success";
};

const mapStatus = (status: string) => {
  // in case of those status map to OPEN
  // otherwise keep the status as it is
  const explicitStatuses = {
    "PARTIAL-EXITING": "OPEN",
    "TOP-UP": "OPEN",
    REBALANCING: "OPEN",
  };

  return explicitStatuses[status] || status;
};

/**
 * Which statuses are still moving.
 *
 * Stated outright rather than inferred from the tone. The pulse used to
 * follow a success tone, which is right for an investment — success there
 * means OPEN, and an open position is live — and wrong for anything else
 * that resolves successfully: a COMPLETED transaction is settled, and a
 * settled thing should not be beating.
 */
const IN_FLIGHT = new Set([
  "PENDING",
  "OPENING",
  "OPEN",
  "EXECUTING",
  "CLOSING",
  "EXITING",
  "PARTIAL-EXITING",
  "TOP-UP",
  "REBALANCING",
]);

const Status: React.FC<Props> = ({ status }) => {
  const tone = statusTone(status);
  const text = mapStatus(status);
  const live = IN_FLIGHT.has(status);

  return (
    <Badge tone={tone} dot={live} pulse={live}>
      {text}
    </Badge>
  );
};

export default Status;
